import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import PersonIcon from "@mui/icons-material/Person";
import { Contact } from "../../../interfaces/DTO/crm/contact";
import { MaritalStatus } from "../../../interfaces/DTO/mislaka_xml_models";
import { formatMaritalStatus } from "../../../utils/enumFormatter";
import { formatName } from "../../../utils/namesFormater";

interface FamilyHeaderRowProps {
  /** a family - its name is shown (and edited) in the row */
  familyName?: string;
  contacts: Contact[];
  /** saves what changed - the family name and/or the marital status of everyone */
  onSave: (changes: { familyName?: string; maritalStatus?: number }) => Promise<void>;
}

const maritalStatusIcon = (status?: number) => {
  switch (status) {
    case MaritalStatus.MARRIED:
    case MaritalStatus.COHABITATING:
      return <FavoriteIcon color="error" fontSize="small" />;
    case MaritalStatus.DIVORCED:
    case MaritalStatus.WIDOWED:
      return <FavoriteBorderIcon fontSize="small" />;
    default:
      return <PersonIcon fontSize="small" />;
  }
};

const MARITAL_STATUS_OPTIONS = Object.values(MaritalStatus).filter((v): v is MaritalStatus => typeof v === "number");

/** the tab's title row - "משפחת X" (a pencil on hover) and the marital status dropdown, centered */
const FamilyHeaderRow: React.FC<FamilyHeaderRowProps> = ({ familyName, contacts, onSave }) => {
  const statuses = Array.from(new Set(contacts.map((c) => c.marital_status ?? null)));
  const sharedStatus = statuses.length === 1 ? statuses[0] ?? undefined : undefined;
  const statusesDiffer = statuses.length > 1;

  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);

  const saveName = async () => {
    if (!name.trim()) return;
    if (name.trim() === familyName) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      await onSave({ familyName: name.trim() });
      setEditingName(false);
    } finally {
      setSavingName(false);
    }
  };

  const saveStatus = async (status: number) => {
    setSavingStatus(true);
    try {
      await onSave({ maritalStatus: status });
    } finally {
      setSavingStatus(false);
    }
  };

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: 2 }}>
      {/* the family name */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, "&:hover .name-edit": { opacity: 1 } }}>
        {familyName !== undefined &&
          (editingName ? (
            <>
              <Typography variant="h5" fontWeight={600}>
                משפחת
              </Typography>
              <TextField
                size="small"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveName();
                  if (e.key === "Escape") setEditingName(false);
                }}
                autoFocus
                sx={{ width: 200 }}
              />
              <IconButton color="primary" onClick={saveName} disabled={savingName || !name.trim()}>
                {savingName ? <CircularProgress size={20} /> : <CheckIcon />}
              </IconButton>
              <IconButton onClick={() => setEditingName(false)} disabled={savingName}>
                <CloseIcon />
              </IconButton>
            </>
          ) : (
            <>
              <Typography variant="h5" fontWeight={600} noWrap>
                משפחת {familyName}
              </Typography>
              <Tooltip title="עריכת שם המשפחה">
                <IconButton
                  className="name-edit"
                  size="small"
                  onClick={() => {
                    setName(familyName);
                    setEditingName(true);
                  }}
                  sx={{ opacity: 0, transition: "opacity .15s" }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          ))}
      </Box>

      {/* the marital status - centered, always a dropdown */}
      <FormControl size="small" sx={{ minWidth: 200 }} disabled={savingStatus}>
        <InputLabel>סטטוס משפחתי</InputLabel>
        <Select
          value={sharedStatus ?? ""}
          label="סטטוס משפחתי"
          onChange={(e) => e.target.value !== "" && saveStatus(Number(e.target.value))}
          renderValue={(v) =>
            sharedStatus === undefined ? (
              ""
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {maritalStatusIcon(Number(v))}
                {formatMaritalStatus(Number(v) as MaritalStatus)}
              </Box>
            )
          }
          endAdornment={savingStatus ? <CircularProgress size={16} sx={{ mx: 3 }} /> : undefined}
        >
          {MARITAL_STATUS_OPTIONS.map((v) => (
            <MenuItem key={v} value={v}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {maritalStatusIcon(v)}
                {formatMaritalStatus(v)}
              </Box>
            </MenuItem>
          ))}
        </Select>
        {statusesDiffer && (
          <Tooltip
            title={contacts
              .map((c) => `${formatName(c)}: ${formatMaritalStatus((c.marital_status ?? MaritalStatus.UNKNOWN) as MaritalStatus)}`)
              .join(" · ")}
          >
            <FormHelperText sx={{ color: "warning.main", textAlign: "center" }}>שונה בין בני הזוג – בחירה תעדכן את שניהם</FormHelperText>
          </Tooltip>
        )}
      </FormControl>

      <Box />
    </Box>
  );
};

export default FamilyHeaderRow;
